import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  CartesianGrid,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts'

import {
  CHART_ANIMATION_FAST,
  CHART_AXIS_LINE,
  CHART_AXIS_TICK,
  CHART_GRID_PROPS,
  CHART_MARGIN,
  CHART_TOOLTIP_STYLE
} from '../../../Components/Charts/chartTheme'
import { cap } from '../../../State'
import { translations } from '../../../translations'
import { formatSeconds } from './utils'

const language = cap.language.state || 'IT'
const t = translations[language]

/**
 * Scatter chart dei tempi di esecuzione dei singoli processi nel tempo
 * @param {{ processes: Array, cacheHint?: string }} props
 * @returns {JSX.Element|null}
 */
export const ProcessTimeScatter = ({ processes = [], cacheHint }) => {
  const navigate = useNavigate()

  const scatterData = useMemo(() => {
    if (!processes.length) return []
    return processes
      .filter(({ executionTime }) => executionTime)
      .map((p) => ({
        createdAt: new Date(p.createdAt).getTime(),
        executionTime: p.executionTime / 1000,
        processId: p.id
      }))
      .sort((a, b) => a.createdAt - b.createdAt)
  }, [processes])

  if (!processes.length) return null

  return (
    <div className="ncard">
      <div className="ncard__head">
        <div className="ncard__ico">
          <i className="pi pi-chart-scatter" style={{ fontSize: 16 }} />
        </div>
        <div>
          <h3 className="ncard__title">{t.pfDistribuzioneTempiProcessi}</h3>
          <div className="ncard__sub">{cacheHint || t.pfOgniPuntoProcesso}</div>
        </div>
      </div>
      <div className="ncard__body">
        <div style={{ height: 350 }}>
          {scatterData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart margin={{ ...CHART_MARGIN, right: 16, bottom: 8 }}>
                <CartesianGrid {...CHART_GRID_PROPS} />
                <XAxis
                  dataKey="createdAt"
                  type="number"
                  domain={['dataMin', 'dataMax']}
                  tickFormatter={(ts) => {
                    const d = new Date(ts)
                    return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`
                  }}
                  tick={CHART_AXIS_TICK}
                  axisLine={CHART_AXIS_LINE}
                  tickLine={false}
                  interval="preserveStartEnd"
                />
                <YAxis
                  dataKey="executionTime"
                  type="number"
                  domain={[0, 'dataMax + 20']}
                  tickFormatter={(v) => formatSeconds(v, true)}
                  tick={CHART_AXIS_TICK}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  content={({ payload }) => {
                    const point = payload?.[0]?.payload
                    if (!point) return null
                    return (
                      <div
                        style={{ ...CHART_TOOLTIP_STYLE, padding: '10px 14px', lineHeight: 1.6 }}
                      >
                        <div style={{ fontWeight: 700, color: 'var(--ink-900)' }}>
                          {t.pfProcessoHash}
                          {point.processId}
                        </div>
                        <div style={{ color: 'var(--ink-700)' }}>
                          {t.pfTempo}
                          {formatSeconds(point.executionTime)}
                        </div>
                        <div style={{ color: 'var(--ink-500)', fontSize: 11 }}>
                          {new Date(point.createdAt).toLocaleString()}
                        </div>
                      </div>
                    )
                  }}
                />
                <Scatter
                  data={scatterData}
                  fill="var(--primary)"
                  fillOpacity={0.65}
                  onClick={({ payload }) =>
                    payload?.processId && navigate(`/process/${payload.processId}`)
                  }
                  cursor="pointer"
                  animationDuration={CHART_ANIMATION_FAST.duration}
                  animationEasing={CHART_ANIMATION_FAST.easing}
                  shape={({ cx, cy }) => (
                    <circle
                      cx={cx}
                      cy={cy}
                      r={5}
                      fill="var(--primary)"
                      fillOpacity={0.65}
                      stroke="var(--surface)"
                      strokeWidth={1.5}
                    />
                  )}
                />
              </ScatterChart>
            </ResponsiveContainer>
          ) : (
            <div
              style={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--ink-400)'
              }}
            >
              {t.pfNessunProcessoTempo}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
